import React from "react";
import {
  check, OCCASIONS, VENUE_TYPES, GUEST_RANGES, SETTINGS, DURATIONS, BUDGET_RANGES,
} from "../../lib/forms";
import { trackFormSubmit } from "../../lib/track";
import { useSiteForm, SiteForm, Field, Choices, Submit, SendError, ThankYou, RequiredNote, useTone } from "./FormKit";

/**
 * Form B — Host Antara, for your occasion. The occasion leads, then the date
 * and the room, then how to reach you. Only the occasion, name, email, phone
 * and city are required; everything else helps us shape the evening but
 * never blocks a send. Posts to the Netlify form "private-event"
 * (lib/forms.js): the name stays so submissions keep landing in the same
 * place in the dashboard.
 */
const RULES = {
  name: [check.required("name")],
  email: [check.email],
  phone: [check.phone()],
  city: [check.required("city")],
  occasion: [check.choose("the occasion")],
};

// Today in India, so the date picker cannot offer a day already gone.
function todayIST() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata", year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());
}

export default function HostForm({ tone = "dark" }) {
  const form = useSiteForm("private-event", {
    initial: { dateFlexible: false },
    rules: RULES,
    onSent: (v) => trackFormSubmit("private-event", { content_type: v.occasion }),
  });

  if (form.status === "done") {
    return (
      <ThankYou form={form} tone={tone} title="Thank you — we have it.">
        We read every enquiry ourselves. Someone from the Antara team will call or write to you
        to talk through the {form.values.occasion ? form.values.occasion.toLowerCase() : "occasion"}.
      </ThankYou>
    );
  }

  return (
    <SiteForm form={form} tone={tone} className="max-w-2xl mx-auto space-y-6">
      <RequiredNote />

      {/* The occasion first: it is what the page is about. */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Field form={form} name="occasion" label="Occasion" required as="select" options={OCCASIONS} placeholder="Choose the occasion" />
        <Field form={form} name="guests" label="Expected guests" optional as="select" options={GUEST_RANGES} />
      </div>

      <DateField form={form} />

      <div className="grid sm:grid-cols-2 gap-5">
        <Field form={form} name="venueType" label="Venue type" optional as="select" options={VENUE_TYPES} />
        <Field form={form} name="duration" label="Approximate duration" optional as="select" options={DURATIONS} />
      </div>

      <Choices form={form} name="setting" label="Indoor or outdoor" optional options={SETTINGS} columns={3} />

      {/* Then how to reach you. */}
      <div className="grid sm:grid-cols-2 gap-5">
        <Field form={form} name="name" label="Name" required autoComplete="name" placeholder="Your name" />
        <Field
          form={form} name="email" label="Email" required
          type="email" inputMode="email" autoComplete="email" autoCapitalize="off" spellCheck={false}
          placeholder="you@example.com"
        />
        <Field
          form={form} name="phone" label="Phone" required
          type="tel" inputMode="tel" autoComplete="tel" placeholder="+91 98765 43210"
        />
        <Field form={form} name="city" label="City" required autoComplete="address-level2" placeholder="Where is the occasion?" />
      </div>

      <Field
        form={form} name="budget" label="Budget range" optional as="select" options={BUDGET_RANGES}
        hint="A rough range helps us suggest the right line-up. Leave it blank, or choose “Prefer to discuss”."
      />

      <Field
        form={form} name="message" label="Anything else you'd like us to know" optional as="textarea"
        maxLength={2000} placeholder="Bhajans the family loves, the feel you're after, timings…"
      />

      <SendError form={form} />
      <Submit form={form}>Send enquiry</Submit>
    </SiteForm>
  );
}

/**
 * Preferred date: a native date picker (the phone's own calendar), plus
 * "My date is flexible", which clears and disables it.
 */
function DateField({ form }) {
  const tone = useTone();
  const flexible = !!form.values.dateFlexible;
  return (
    <div>
      <Field
        form={form} name="preferredDate" label="Preferred date" optional
        type="date" min={todayIST()} disabled={flexible}
        hint={flexible ? "No problem — we'll work out a date together." : undefined}
      />
      <label
        className="mt-3 inline-flex items-center gap-2.5 font-body text-[15px] cursor-pointer"
        style={{ color: tone === "dark" ? "#E8D9C5" : "#4A3826" }}
      >
        <input
          type="checkbox"
          name="dateFlexible"
          checked={flexible}
          onChange={(e) => {
            form.setValue("dateFlexible", e.target.checked);
            if (e.target.checked) form.setValue("preferredDate", "");
          }}
          className="h-[18px] w-[18px] accent-[#B8923E]"
          data-testid="private-event-dateFlexible"
        />
        My date is flexible
      </label>
    </div>
  );
}
